// Draft of one project catalog in "הגדרות מסירות" (H-2): the list as stored plus the user's
// edits, saved together as one list. Entries are deactivated, never deleted.
import {
  CATALOG_LIMITS,
  CONTACT_KINDS,
  type CatalogKind,
  type CatalogListResponse,
  type CatalogSaveRequest,
  EMAIL_PATTERN,
  PHONE_PATTERN,
  type SupplyType,
} from '@sivan/shared';

export interface CatalogRow {
  /** Stable key in the editor; equals `id` for stored entries. */
  readonly key: string;
  readonly id: string | null;
  readonly name: string;
  readonly active: boolean;
  readonly contactName: string;
  readonly contactPhone: string;
  readonly contactEmail: string;
  /** Contractors: their work categories (ids). */
  readonly categoryIds: readonly string[];
  /** Work categories: the contractor suggested for new findings (H-Checklist). */
  readonly defaultContractorId: string | null;
  /** Suppliers: what they supply (DR-188); null until chosen. */
  readonly supplyType: SupplyType | null;
}

export type RowErrors = Partial<
  Record<'name' | 'contactPhone' | 'contactEmail' | 'contactName' | 'supplyType', string>
>;

let counter = 0;
const newKey = (): string => {
  counter += 1;
  return `new-${String(counter)}`;
};

export const blankRow = (name = ''): CatalogRow => ({
  key: newKey(),
  id: null,
  name,
  active: true,
  contactName: '',
  contactPhone: '',
  contactEmail: '',
  categoryIds: [],
  defaultContractorId: null,
  supplyType: null,
});

export function rowsFrom(list: CatalogListResponse): CatalogRow[] {
  return list.items.map((item) => ({
    key: item.id,
    id: item.id,
    name: item.name,
    active: item.active,
    contactName: item.contact?.contactName ?? '',
    contactPhone: item.contact?.contactPhone ?? '',
    contactEmail: item.contact?.contactEmail ?? '',
    categoryIds: [...item.categoryIds].sort(),
    defaultContractorId: item.defaultContractorId,
    supplyType: item.supplyType,
  }));
}

const orNull = (text: string): string | null => (text.trim() === '' ? null : text.trim());

/**
 * The save request. Contacts are sent only for contractors and only by users who see them, so a
 * user without contact access never overwrites contacts they cannot see.
 */
export function toRequest(
  rows: readonly CatalogRow[],
  listVersion: string,
  kind: CatalogKind,
  contacts: boolean,
): CatalogSaveRequest {
  return {
    listVersion,
    items: rows.map((row) => ({
      id: row.id,
      name: row.name.trim(),
      active: row.active,
      ...(kind === 'contractor' ? { categoryIds: [...row.categoryIds].sort() } : {}),
      ...(kind === 'work_category' ? { defaultContractorId: row.defaultContractorId } : {}),
      ...(kind === 'supplier' && row.supplyType !== null ? { supplyType: row.supplyType } : {}),
      ...(CONTACT_KINDS.includes(kind) && contacts
        ? {
            contactName: orNull(row.contactName),
            contactPhone: orNull(row.contactPhone),
            contactEmail: orNull(row.contactEmail),
          }
        : {}),
    })),
  };
}

export function isDirty(
  rows: readonly CatalogRow[],
  list: CatalogListResponse,
  kind: CatalogKind,
): boolean {
  const request = (r: readonly CatalogRow[]) =>
    JSON.stringify(toRequest(r, list.listVersion, kind, list.permissions.contacts));
  return request(rows) !== request(rowsFrom(list));
}

/** Errors per row key; empty when the list can be saved. */
export function validateRows(
  rows: readonly CatalogRow[],
  kind: CatalogKind,
): Record<string, RowErrors> {
  const errors: Record<string, RowErrors> = {};
  const seen = new Map<string, string>();
  for (const row of rows) {
    const e: RowErrors = {};
    const name = row.name.trim();
    if (name === '') e.name = 'שדה חובה';
    else if (name.length > CATALOG_LIMITS.name) e.name = `עד ${String(CATALOG_LIMITS.name)} תווים`;
    else if (seen.has(name.toLowerCase())) e.name = 'השם כבר קיים ברשימה';
    seen.set(name.toLowerCase(), row.key);
    if (kind === 'supplier' && row.supplyType === null) e.supplyType = 'יש לבחור סוג אספקה';
    if (CONTACT_KINDS.includes(kind)) {
      const phone = row.contactPhone.trim();
      if (phone !== '' && !PHONE_PATTERN.test(phone)) e.contactPhone = 'מספר טלפון לא תקין';
      const email = row.contactEmail.trim();
      if (email !== '' && !EMAIL_PATTERN.test(email)) e.contactEmail = 'כתובת דוא"ל לא תקינה';
      if (row.contactName.trim().length > CATALOG_LIMITS.contactName)
        e.contactName = `עד ${String(CATALOG_LIMITS.contactName)} תווים`;
    }
    if (Object.keys(e).length > 0) errors[row.key] = e;
  }
  return errors;
}

/**
 * Rows from pasted text: one entry per line. For contractors and suppliers, columns separated by tabs (as copied
 * from Excel) are name, contact person, phone and email. Blank lines are skipped.
 */
export function parseBulk(text: string, kind: CatalogKind): CatalogRow[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.split('\t').map((cell) => cell.trim()))
    .filter((cells) => (cells[0] ?? '') !== '')
    .map((cells) => {
      const row = blankRow(cells[0]);
      if (!CONTACT_KINDS.includes(kind)) return row;
      return {
        ...row,
        contactName: cells[1] ?? '',
        contactPhone: cells[2] ?? '',
        contactEmail: cells[3] ?? '',
      };
    });
}

/** Moves a row up (-1) or down (+1); unchanged at the ends. */
export function move(rows: readonly CatalogRow[], key: string, delta: -1 | 1): CatalogRow[] {
  const index = rows.findIndex((r) => r.key === key);
  const target = index + delta;
  if (index < 0 || target < 0 || target >= rows.length) return [...rows];
  const next = [...rows];
  const [row] = next.splice(index, 1);
  if (row !== undefined) next.splice(target, 0, row);
  return next;
}
